import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as ProductsAPI from '../api/productsAPI';
import type {
  Product,
  ProductApplication,
  ProductWebfunnelLink,
  WebLinkType,
} from './types';
import type { ApplicationType } from './types';

interface State {
  products: Product[];
  status: DataStatus;
}

const initialState: State = {
  products: [],
  status: DataStatus.idle,
};

export const productsSlice = createAppSlice({
  name: 'products',
  initialState,
  reducers: (create) => ({
    setupProducts: create.asyncThunk(
      (_: void) => {
        return ProductsAPI.getProducts();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.products = action.payload;
          state.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.status = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (isLoading(state.products.status)) {
              return false;
            }
          },
        },
      },
    ),
    addProduct: create.asyncThunk<{ name: string }, Product>(
      (product) => {
        return ProductsAPI.addProduct(product);
      },
      {
        fulfilled: (state, action) => {
          state.products.push(action.payload);
        },
      },
    ),
    addProductApplication: create.asyncThunk<
      | {
          productId: string;
          integrationId: string;
          platform: ApplicationType.ios;
          bundleId: string;
          appstoreId: string;
          name: string;
          logoUrl: string;
        }
      | {
          productId: string;
          integrationId: string;
          platform: ApplicationType.android;
          bundleId: string;
          name: string;
          logoUrl: string;
        }
      | {
          productId: string;
          platform: ApplicationType.web;
          webUrl: string;
          name: string;
        },
      ProductApplication
    >(
      (params) => {
        return ProductsAPI.addProductApplication(params);
      },
      {
        fulfilled: (state, action) => {
          state.products
            .find((product) => product.id === action.payload.productId)
            ?.applications.push(action.payload);
        },
      },
    ),
    deleteProductApplication: create.asyncThunk<
      { productId: string; id: string },
      void
    >(
      (params) => {
        return ProductsAPI.deleteProductApplication(params);
      },
      {
        fulfilled: (state, action) => {
          state.products = state.products.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                applications: product.applications.filter(
                  (application) => application.id !== action.meta.arg.id,
                ),
              };
            }

            return product;
          });
        },
      },
    ),
    setupWebfunnelLinks: create.asyncThunk<
      { productId: string },
      ProductWebfunnelLink[]
    >(
      (params) => {
        return ProductsAPI.getWebfunnelLinks(params);
      },
      {
        pending: (state, action) => {
          state.products = state.products.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webfunnelLinksDataStatus: DataStatus.loading,
              };
            }

            return product;
          });
        },
        fulfilled: (state, action) => {
          state.products = state.products.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webfunnelLinks: action.payload,
                webfunnelLinksDataStatus: DataStatus.finished,
              };
            }

            return product;
          });
        },
        options: {
          condition: ({ productId }, { getState }) => {
            const state = getState() as RootState;
            const product = state.products.products.find(
              (product) => product.id === productId,
            );

            if (
              !product ||
              isLoading(product.webfunnelLinksDataStatus) ||
              isFinished(product.webfunnelLinksDataStatus)
            ) {
              return false;
            }
          },
        },
      },
    ),
    addWebfunnelLink: create.asyncThunk<
      { productId: string; webUrl: string; linkType: WebLinkType },
      ProductWebfunnelLink
    >(
      (params) => {
        return ProductsAPI.addWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          state.products
            .find((product) => product.id === action.payload.productId)
            ?.webfunnelLinks.push(action.payload);
        },
      },
    ),
    deleteWebfunnelLink: create.asyncThunk<
      { productId: string; id: string },
      void
    >(
      (params) => {
        return ProductsAPI.deleteWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          state.products = state.products.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webfunnelLinks: product.webfunnelLinks.filter(
                  (link) => link.id !== action.meta.arg.id,
                ),
              };
            }

            return product;
          });
        },
      },
    ),
    updateWebfunnelLink: create.asyncThunk<
      { productId: string; id: string; webUrl: string; linkType: WebLinkType },
      ProductWebfunnelLink
    >(
      (params) => {
        return ProductsAPI.updateWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          state.products = state.products.map((product) => {
            if (product.id === action.payload.productId) {
              return {
                ...product,
                webfunnelLinks: product.webfunnelLinks.map((link) => {
                  if (link.id === action.payload.id) {
                    return action.payload;
                  }

                  return link;
                }),
              };
            }

            return product;
          });
        },
      },
    ),
  }),
  selectors: {
    selectProducts: (state) => {
      return state.products;
    },
    selectIsLoading: (state) => {
      return isLoading(state.status);
    },
    selectIsFinished: (state) => {
      return isFinished(state.status);
    },
    selectProductApplications: createSelector(
      (state) => state.products,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        return (
          products?.find((product: Product) => product.id === productId)
            ?.applications || []
        );
      },
    ),
    selectProductWebfunnelLinks: createSelector(
      (state) => state.products,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        return (
          products?.find((product: Product) => product.id === productId)
            ?.webfunnelLinks || []
        );
      },
    ),
    selectIsLoadingWebfunnelLinks: createSelector(
      (state) => state.products,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        const product = products?.find(
          (product: Product) => product.id === productId,
        );

        return !product || isLoading(product.webfunnelLinksDataStatus);
      },
    ),
  },
});

export const {
  selectProducts,
  selectIsLoading,
  selectIsFinished,
  selectProductApplications,
  selectProductWebfunnelLinks,
  selectIsLoadingWebfunnelLinks,
} = productsSlice.selectors;
export const {
  setupProducts,
  addProduct,
  addProductApplication,
  deleteProductApplication,
  setupWebfunnelLinks,
  addWebfunnelLink,
  deleteWebfunnelLink,
  updateWebfunnelLink,
} = productsSlice.actions;
